import type { PartnerOption } from '../data/getGroupPartners';

/**
 * One line of the cost breakdown, as structured data. Formatting and translation
 * happen in the rendering component (`TransportCostCard`) so the copy follows the
 * active locale instead of being pinned to Dutch/nl-BE here.
 */
export type TransportBreakdownEntry =
    /** `distanceKm` is the billable distance, i.e. already doubled for the return trip. */
    | { kind: 'distanceRate'; distanceKm: number; costPerKm: number }
    | { kind: 'roundTrip'; oneWayKm: number }
    | { kind: 'extraCost'; extraEUR: number }
    | { kind: 'perParticipants'; multiplier: number; batchSize: number };

export type TransportEstimate = {
    /** Transport cost in EUR, exactly as the partner's own tariff rules produce it. */
    costEUR: number;
    /** Structured breakdown entries, translated + formatted by the consumer. */
    breakdown: readonly TransportBreakdownEntry[];
    /** km used for the calculation, or null if unknown */
    distanceKm: number | null;
    /** true when no distance is known yet, so `costEUR` is not a real amount */
    isFallback: boolean;
    /**
     * Whether the partner actually has transport tariffs configured. When false we have no
     * rate and no fixed fee to work from (`activity_transport_cost` is empty), so `costEUR`
     * is 0 by absence of data — not because the trip is free. Callers must not present that
     * as a price, nor compare it against another partner's real tariff.
     */
    hasTariff: boolean;
};

/** The partner drives there and back, so the charged distance is twice the trip. */
const ROUND_TRIP_FACTOR = 2;

/**
 * Funkey's margin on transport, applied to the partner's rates rather than to the total: the
 * tooltip lists the per-km rate and the fixed fee, and a total those lines do not add up to
 * gives the margin away. Partners never see it — their price simulator is separate code and
 * quotes their own tariff.
 */
const TRANSPORT_UPLIFT = 1.2;

/**
 * Pick the applicable tier for a given distance: the tier with the highest `distance`
 * (inclusive lower bound) that the trip still reaches. Falls back to the lowest-threshold
 * tier when the distance is below every tier — the partner simulator does the same (a 19 km
 * trip uses "rule 1" even when rule 2 only starts at 20 km). Order-independent, so callers
 * don't have to pre-sort the tiers.
 */
function pickTier(
    distanceKm: number,
    tiers: PartnerOption['transportCostTiers'],
): PartnerOption['transportCostTiers'][number] | null {
    if (!tiers.length) return null;
    let chosen: PartnerOption['transportCostTiers'][number] | null = null;
    let lowest: PartnerOption['transportCostTiers'][number] = tiers[0]!;
    for (const tier of tiers) {
        if (tier.distance < lowest.distance) lowest = tier;
        if (distanceKm >= tier.distance && (!chosen || tier.distance > chosen.distance)) {
            chosen = tier;
        }
    }
    return chosen ?? lowest;
}

/**
 * Transport cost for a partner-to-us trip, using the partner's own tariff rules.
 *
 * The rate itself mirrors `calculateTransportPrice` in the partner portal's transport form
 * (`portal/transport/TransportFormWithState.tsx`) — the price simulator partners see while
 * configuring their rules:
 *
 *     (costPerKm × billableKm + extraCost) × ceil(participants / batchSize)
 *
 * The one difference is `billableKm`: the partner drives to the customer *and back*, so the
 * charged distance is twice the distance we show on the map. Only the kilometres are
 * doubled — `extraCost` is a fixed fee per booking, not per leg, so doubling it would charge
 * a €20 "vaste meerprijs" twice.
 *
 * The tier lookup deliberately uses the **one-way** distance: a rule reading "vanaf 26 km"
 * describes how far away the customer is, which is the number the partner had in mind when
 * configuring it and the number we show them.
 *
 * Both rates carry `TRANSPORT_UPLIFT`, so the customer's price is the partner's plus Funkey's
 * margin while every line in the breakdown still sums to the amount shown.
 *
 * `transportMultiplier` is a **batch size** (e.g. 60 means "per group of 60 participants").
 * When it is 0 (disabled) the multiplier is always 1. There is deliberately no minimum
 * charge or rounding: the partner configures a fixed fee via `extraCost` if they want one, and
 * the table has no minimum column at all.
 */
export function calcTransportCost(args: {
    distanceKm: number | null;
    tiers: PartnerOption['transportCostTiers'];
    transportMultiplier: number;
    /** Number of participants. Defaults to 1. */
    participants?: number;
}): TransportEstimate {
    const { tiers, transportMultiplier, participants = 1 } = args;
    const batchSize = transportMultiplier > 0 ? transportMultiplier : 0;
    const multiplier = batchSize > 0 ? Math.ceil(participants / batchSize) : 1;

    const tier = args.distanceKm == null ? null : pickTier(args.distanceKm, tiers);
    if (args.distanceKm == null || !tier) {
        return {
            costEUR: 0,
            breakdown: [],
            distanceKm: args.distanceKm,
            isFallback: args.distanceKm == null,
            hasTariff: tiers.length > 0,
        };
    }

    const billableKm = args.distanceKm * ROUND_TRIP_FACTOR;
    const costPerKm = tier.costPerKm * TRANSPORT_UPLIFT;
    const extraCost = tier.extraCost * TRANSPORT_UPLIFT;
    return {
        costEUR: (costPerKm * billableKm + extraCost) * multiplier,
        breakdown: [
            ...(costPerKm
                ? ([
                      { kind: 'distanceRate', distanceKm: billableKm, costPerKm },
                      { kind: 'roundTrip', oneWayKm: args.distanceKm },
                  ] as const)
                : []),
            ...(extraCost ? [{ kind: 'extraCost', extraEUR: extraCost } as const] : []),
            ...(batchSize > 0 ? [{ kind: 'perParticipants', multiplier, batchSize } as const] : []),
        ],
        distanceKm: args.distanceKm,
        isFallback: false,
        hasTariff: true,
    };
}
